/**
 * Home setup prompts (Sep 2026): at most ONE of these shows, top of Home.
 *   1. Install the app — on a phone/tablet browser tab. One-tap Install on
 *      Android/Chromium (beforeinstallprompt), step-by-step on iPhone/iPad.
 *   2. Turn on notifications — where push works on this device, the user may
 *      use it (canUsePush) and it isn't on yet. On iPhone push needs the
 *      installed app, so (1) comes first there.
 * "Not now" hides a prompt for 14 days in this browser; the Profile card stays
 * the permanent place to change push.
 */
import { useEffect, useState } from 'react';
import { Bell, Download, Share, SquarePlus, X } from 'lucide-react';
import { canUsePush } from '@daaily/shared';
import { Icon } from '../design/Icon';
import { btnPrimary, btnGhost, card } from '../design/ui';
import { useProfile } from '../profile/ProfileProvider';
import { enablePush, isPushOnHere, PUSH_STEP_LABEL, pushAvailability, type PushStep } from '../push/push';
import {
  canOneTapInstall, dismissPrompt, isIosDevice, isIosNonSafari, oneTapInstall, onInstallAvailabilityChange,
  promptDismissed, shouldOfferInstall,
} from './installPrompt';

export function SetupPrompts() {
  const { state } = useProfile();
  const email = state.status === 'ready' ? state.user.email : '';
  const [, force] = useState(0);
  const [hidden, setHidden] = useState<'install' | 'push' | null>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<PushStep | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => onInstallAvailabilityChange(() => force((n) => n + 1)), []);
  if (state.status !== 'ready' || !email) return null;

  const showInstall = hidden !== 'install' && shouldOfferInstall() && !promptDismissed('install', email);
  const pushAllowed = canUsePush(email, state.isAdmin, state.config);
  const showPush = !showInstall && hidden !== 'push' && pushAllowed && pushAvailability() === 'ok'
    && !isPushOnHere() && !promptDismissed('push', email)
    && (typeof Notification === 'undefined' || Notification.permission !== 'denied');

  if (done) {
    return <p role="status" className={`${card} px-4 py-3 text-sm text-success`}>{done}</p>;
  }
  if (!showInstall && !showPush) return null;

  const notNow = (kind: 'install' | 'push') => { dismissPrompt(kind, email, 14); setHidden(kind); };

  if (showInstall) {
    const ios = isIosDevice();
    return (
      <section aria-labelledby="install-h" className={`${card} relative p-4 pr-11`}>
        <button type="button" onClick={() => notNow('install')} aria-label="Not now" className="absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-faint hover:text-fg hover:bg-surface-2">
          <Icon as={X} size={16} />
        </button>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-2 text-fg-muted"><Icon as={Download} size={16} /></span>
          <div className="min-w-0 flex-1 space-y-2">
            <h2 id="install-h" className="text-sm font-semibold text-fg">Put DAAily Hub on your home screen</h2>
            <p className="text-sm text-fg-muted">
              It opens like an app, full screen, with Face ID or fingerprint lock{pushAllowed ? ', and it can send you notifications when something needs you' : ''}.
            </p>
            {canOneTapInstall() ? (
              <button type="button" className={btnPrimary} disabled={busy} onClick={async () => {
                setBusy(true); setErr(null);
                try { if (await oneTapInstall()) setDone('Installed. Open DAAily Hub from your home screen from now on.'); }
                catch (e) { setErr(e instanceof Error ? e.message : 'Install failed.'); }
                finally { setBusy(false); }
              }}>Install app</button>
            ) : ios && isIosNonSafari() ? (
              <p className="text-sm text-fg">Open <span className="font-medium">hub.daaily.com</span> in <span className="font-medium">Safari</span> first: only Safari can add apps to the home screen on iPhone and iPad.</p>
            ) : ios ? (
              <ol className="list-decimal pl-5 space-y-1 text-sm text-fg">
                <li>Tap the Share button <Icon as={Share} size={14} className="inline -mt-0.5 text-fg-muted" /> in Safari’s toolbar.</li>
                <li>Scroll down and tap <span className="font-medium">Add to Home Screen</span> <Icon as={SquarePlus} size={14} className="inline -mt-0.5 text-fg-muted" />.</li>
                <li>Tap <span className="font-medium">Add</span>, then open DAAily Hub from your home screen.</li>
              </ol>
            ) : (
              <ol className="list-decimal pl-5 space-y-1 text-sm text-fg">
                <li>Open your browser menu (⋮ in Chrome).</li>
                <li>Tap <span className="font-medium">Install app</span> or <span className="font-medium">Add to Home screen</span>.</li>
                <li>Open DAAily Hub from your home screen.</li>
              </ol>
            )}
            {err && <p className="text-sm text-danger">{err}</p>}
            <button type="button" className={`${btnGhost} -ml-2`} onClick={() => notNow('install')}>Not now</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="push-h" className={`${card} relative p-4 pr-11`}>
      <button type="button" onClick={() => notNow('push')} aria-label="Not now" className="absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-faint hover:text-fg hover:bg-surface-2">
        <Icon as={X} size={16} />
      </button>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-2 text-fg-muted"><Icon as={Bell} size={16} /></span>
        <div className="min-w-0 flex-1 space-y-2">
          <h2 id="push-h" className="text-sm font-semibold text-fg">Get notified when something needs you</h2>
          <p className="text-sm text-fg-muted">
            A notification on this device for approvals, signatures, receipt requests and decisions on your requests. It never shows names, amounts or dates. You can turn it off any time in your profile.
          </p>
          {err && <p className="text-sm text-danger">{err}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={btnPrimary} disabled={busy} onClick={async () => {
              setBusy(true); setErr(null);
              try { await enablePush(setStep); setDone('Notifications are on for this device.'); }
              catch (e) { setErr(e instanceof Error ? e.message : 'Could not turn on notifications.'); }
              finally { setBusy(false); setStep(null); }
            }}>{busy ? (step ? PUSH_STEP_LABEL[step] : 'Turning on…') : 'Turn on notifications'}</button>
            <button type="button" className={btnGhost} onClick={() => notNow('push')}>Not now</button>
          </div>
        </div>
      </div>
    </section>
  );
}
